// Build CASL ability from user role/permissions (backend format: module.feature.access)
import { AbilityBuilder, createMongoAbility } from '@casl/ability';
import { parsePermission, canAccessDepartment, getDepartmentFromSubject, getUserMetadata } from './constants';

/**
 * Builds CASL ability from the user's permissions array.
 * Backend permission format: module.feature.access (e.g. sales.leads.view, projects.details.update).
 * See docs/backend_docs/RBAC_Roles_And_Permissions.md.
 *
 * Expected user object:
 * {
 *   role: 'company-admin' | 'super-admin' | ...,
 *   permissions: ['saas.tenants.*', 'sales.leads.view', 'projects.details.view', ...],
 *   department: optional,
 *   adminRoles: optional (default includes super-admin, company-admin),
 *   resourceDepartments: optional (for department-scoped resources)
 * }
 */
const buildAbilityFromUser = (user) => {
    const { can, cannot, build } = new AbilityBuilder(createMongoAbility);

    if (!user) {
        // No user = guest permissions (no access)
        return build();
    }

    const {
        department: userDepartment,
        role: userRole,
        permissions: userPermissions,
        adminRoles,
        resourceDepartments,
    } = getUserMetadata(user);

    // Build only from permissions (no bypass; backend sends explicit permissions for all roles)
    // Process permissions from backend (module.feature.access)
    userPermissions.forEach((permissionString) => {
        const parsed = parsePermission(permissionString);
        if (parsed) {
            const [action, subject] = parsed;

            // Check if this resource belongs to a specific department
            const subjectDepartment = getDepartmentFromSubject(subject, resourceDepartments);

            if (subjectDepartment) {
                // Resource belongs to a department - check accesssoc
                if (canAccessDepartment(userDepartment, subjectDepartment, userRole, adminRoles)) {
                    can(action, subject);
                }
                // If department check fails, permission is denied (implicit)
            } else {
                // Resource doesn't belong to a specific department (department-agnostic)
                // Examples: Dashboard, Notifications, Calendar, Profile, Settings
                // Allow access if user has the permission
                can(action, subject);
            }
        }
    });

    // Admin roles get full access
    if (adminRoles.includes(userRole) || userRole === 'Company Admin' || userRole === 'Super Admin' || userRole === 'company-admin' || userRole === 'super-admin') {
        can('manage', 'all');
    }

    // Common permissions for all authenticated users (regardless of department)
    if (userRole !== 'guest') {
        can('read', 'Profile');
        can('update', 'Profile');
        can('read', 'Settings');
        can('update', 'Settings');
    }

    return build();
};

export { buildAbilityFromUser };
